// components/FormulesRepartitions/ClassementTableauModal.jsx
// ------------------------------------------------------------
// Modale "Classement" : 🥇 🥈 🥉 🥉
// - Reconstitue le bracket à partir de firstRoundSeed + combats
// - 1er/2e depuis la finale
// - 2 troisièmes = perdants des 1/2 finales
// - Affiche "(en attente des combats)" si non déterminé
// ------------------------------------------------------------

import React, { useMemo } from "react";

const Backdrop = ({ children }) => (
  <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
    <div className="relative w-full max-w-xl">{children}</div>
  </div>
);

const MedalCard = ({ medal, title, competitor, accent }) => {
  const isPending = !competitor;
  return (
    <div className={`rounded-2xl border shadow-sm p-4 flex items-center gap-4 ${accent.bg} ${accent.border}`}>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${accent.iconBg}`}>
        {medal}
      </div>
      <div className="flex-1">
        <p className={`text-sm font-semibold ${accent.title}`}>{title}</p>
        <p className={`text-base font-bold`}>
          {isPending
            ? "(en attente des combats)"
            : `${competitor.nom_competiteur ?? ""} ${competitor.prenom_competiteur ?? ""}`}
        </p>
        <p className="text-sm text-gray-500">
          {isPending ? "\u00A0" : (competitor.club_competiteur || "\u00A0")}
        </p>
      </div>
    </div>
  );
};

const accents = {
  gold:   { bg: "bg-amber-50",  border: "border-amber-200",  iconBg: "bg-amber-100",  title: "text-amber-700" },
  silver: { bg: "bg-slate-50",  border: "border-slate-200",  iconBg: "bg-slate-100",  title: "text-slate-700" },
  bronze: { bg: "bg-orange-50", border: "border-orange-200", iconBg: "bg-orange-100", title: "text-orange-700" },
};

// Helpers combats
const findCombat = (combats, id1, id2) =>
  combats.find(
    (c) =>
      (c.competiteur_id1 === id1 && c.competiteur_id2 === id2) ||
      (c.competiteur_id1 === id2 && c.competiteur_id2 === id1)
  );

const getWinnerId = (combat) => {
  if (!combat) return null;
  if (combat.resultat_competiteur1 === "V") return combat.competiteur_id1;
  if (combat.resultat_competiteur2 === "V") return combat.competiteur_id2;
  return null;
};

const nextRoundFromList = (list, combats, byId) => {
  const next = [];
  for (let i = 0; i < list.length; i += 2) {
    const c1 = list[i];
    const c2 = list[i + 1] || null;

    if (c1 && !c2) { next.push(c1); continue; }
    if (!c1 && c2) { next.push(c2); continue; }

    const combat = findCombat(combats, c1?.id, c2?.id);
    if (combat) {
      const wId = getWinnerId(combat);
      next.push(wId ? byId.get(wId) : { pending: true });
    } else {
      next.push({ pending: true });
    }
  }
  return next;
};

const ClassementTableauModal = ({
  isOpen,
  onClose,
  combats = [],
  firstRoundSeed = [],
  competiteurs = [],
}) => {
  const byId = useMemo(
    () => new Map(competiteurs.map((c) => [c.id, c])),
    [competiteurs]
  );

  // Reconstitue toutes les colonnes à partir de la seed
  const { semiPairs, finalPair } = useMemo(() => {
    let current = Array.isArray(firstRoundSeed) ? firstRoundSeed : [];
    const columns = [];
    while (current.length > 1) {
      columns.push(current);
      current = nextRoundFromList(current, combats, byId);
    }
    // colonnes: [R1, R2, ..., R(last-1), R(last)]
    // on veut les 1/2 finales = avant-dernier tour
    const semis = columns.length >= 2 ? columns[columns.length - 2] : [];
    const fin   = columns.length >= 1 ? columns[columns.length - 1] : [];
    return {
      semiPairs: semis.length ? [ [semis[0], semis[1]], [semis[2], semis[3]] ] : [],
      finalPair: fin.length ? [fin[0], fin[1]] : [null, null],
    };
  }, [firstRoundSeed, combats, byId]);

  // Calcule 1/2/3/3
  const classement = useMemo(() => {
    let first = null, second = null, thirdA = null, thirdB = null;

    // 1er/2e via la finale
    const [f1, f2] = finalPair;
    if (f1 && f2 && f1.id && f2.id) {
      const finalCombat = findCombat(combats, f1.id, f2.id);
      if (finalCombat) {
        const wId = getWinnerId(finalCombat);
        if (wId) {
          const winner  = byId.get(wId);
          const loserId = wId === finalCombat.competiteur_id1 ? finalCombat.competiteur_id2 : finalCombat.competiteur_id1;
          first  = winner || null;
          second = byId.get(loserId) || null;
        }
      }
    }

    // Deux 3e = perdants des 1/2
    if (semiPairs.length === 2) {
      const losers = [];
      for (const [s1, s2] of semiPairs) {
        if (s1 && s2 && s1.id && s2.id) {
          const semiCombat = findCombat(combats, s1.id, s2.id);
          if (semiCombat) {
            const wId = getWinnerId(semiCombat);
            if (wId) {
              const lId = wId === semiCombat.competiteur_id1 ? semiCombat.competiteur_id2 : semiCombat.competiteur_id1;
              const loserObj = byId.get(lId);
              if (loserObj) losers.push(loserObj);
            }
          }
        }
      }
      thirdA = losers[0] || null;
      thirdB = losers[1] || null;
    }

    return { first, second, thirdA, thirdB };
  }, [semiPairs, finalPair, combats, byId]);

  if (!isOpen) return null;

  return (
    <Backdrop>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold">Classement — Tableau</h3>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm"
          >
            Fermer
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <MedalCard medal="🥇" title="1er" competitor={classement.first}  accent={accents.gold} />
          <MedalCard medal="🥈" title="2e" competitor={classement.second} accent={accents.silver} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <MedalCard medal="🥉" title="3e" competitor={classement.thirdA} accent={accents.bronze} />
            <MedalCard medal="🥉" title="3e" competitor={classement.thirdB} accent={accents.bronze} />
          </div>
        </div>
      </div>
    </Backdrop>
  );
};

export default ClassementTableauModal;
